import { FolderContent, Folder as FolderType, Asset } from '@/api/generatedApi';
import { useEffect } from 'react';
import { AssetActionsMenuContent } from '@/components/AssetsPage/AssetActionsMenuContent';
import { DeleteItemMenuContent } from '@/components/AssetsPage/DeleteItemMenuContent';
import { FolderActionsMenuContent } from '@/components/AssetsPage/FolderActionsMenuContent';
import { NewItemMenuContent } from '@/components/AssetsPage/NewItemMenuContent';
import { CopyMenuItem } from '@/components/ContextMenu/CopyMenuItem';
import { OpenInNewTabMenuItem } from '@/components/ContextMenu/OpenInNewTabMenuItem';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopiableColumn } from '@/types/copiableColumn';
import { ContextMenuOption, ALL_CONTEXT_MENU_OPTIONS } from '@/types/contextMenuOptions';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useSingleItemModals } from './useSingleItemModals';
import { useBulkMoveModal } from './useBulkMoveModal';
import { useBulkDeleteModal } from './useBulkDeleteModal';
import { BulkActionsMenuContent } from './BulkActionsMenuContent';
import { SingleItemModals } from './SingleItemModals';
import { BulkActionModals } from './BulkActionModals';

export const ContentsTableContextMenu = <T extends FolderContent>({
  anchorPosition,
  onClose,
  clickedItem,
  clickedField,
  columns,
  parentFolder,
  handleFileUpload,
  handleFolderUpload,
  setTargetFolderId,
  isSearch,
  onDeleted,
  allowedOptions = ALL_CONTEXT_MENU_OPTIONS,
  selectedRows = [],
  onBulkActionComplete,
}: {
  anchorPosition: { left: number; top: number } | undefined;
  onClose: () => void;
  clickedItem: T | undefined;
  clickedField?: string;
  columns?: (GridColDef<T> & Partial<CopiableColumn<T>>)[];
  parentFolder?: FolderType;
  handleFileUpload?: () => void;
  handleFolderUpload?: () => void;
  setTargetFolderId?: (folderId: string | undefined) => void;
  isSearch?: boolean;
  onDeleted?: (item: T) => void;
  allowedOptions?: ContextMenuOption[];
  selectedRows?: T[];
  onBulkActionComplete?: () => void;
}) => {
  const { permissions } = useGetCurrentUser();
  const singleItemModals = useSingleItemModals<T>({ onDeleted });
  const bulkMove = useBulkMoveModal<T>({ selectedRows, onClose, onComplete: onBulkActionComplete });
  const bulkDelete = useBulkDeleteModal<T>({ selectedRows, onClose, onComplete: onBulkActionComplete });

  const isClickedItemInSelection = clickedItem && selectedRows.some(row => row.id === clickedItem.id);
  const isBulkMode = selectedRows.length > 1 && isClickedItemInSelection;
  const bulkSelectionCount = isBulkMode ? selectedRows.length : 0;

  const canCopy = allowedOptions.includes('copy');
  const canCreate = allowedOptions.includes('create');
  const canDelete = allowedOptions.includes('delete');
  const canBulkMove = isBulkMode && permissions.canCreateFolders;
  const canBulkDelete = isBulkMode && permissions.canDeleteAssets;

  const showNewItemContent = canCreate && !isSearch && handleFileUpload && handleFolderUpload && !isBulkMode;
  const showDeleteItemContent = canDelete && !!clickedItem && !isBulkMode;
  const copyValue =
    canCopy && clickedItem && clickedField && columns && !isBulkMode
      ? columns.find(col => col.field === clickedField)?.getCopyValue?.(clickedItem)
      : undefined;
  const hasCopyValue = !!copyValue;
  const showBulkActions = isBulkMode && (canBulkMove || canBulkDelete);
  const showOpenInNewTab = !!clickedItem && !isBulkMode && clickedField === 'name';
  const showAssetActions = !!clickedItem && clickedItem.contentType === 'asset' && !isBulkMode;
  const showContextMenu =
    showOpenInNewTab ||
    showNewItemContent ||
    showDeleteItemContent ||
    hasCopyValue ||
    showBulkActions ||
    showAssetActions;

  useEffect(() => {
    if (anchorPosition && setTargetFolderId) {
      const currentTargetId = clickedItem?.contentType === 'folder' ? clickedItem.id : parentFolder?.id;
      setTargetFolderId(currentTargetId);
    }
  }, [anchorPosition, clickedItem?.id, clickedItem?.contentType, parentFolder?.id, setTargetFolderId]);

  const getTargetFolder = (): FolderType | undefined =>
    clickedItem?.contentType === 'folder' ? (clickedItem as FolderType) : parentFolder;

  return (
    <>
      {showContextMenu && (
        <BaseContextMenu anchorPosition={anchorPosition} onClose={onClose}>
          {isBulkMode ? (
            <BulkActionsMenuContent
              itemCount={bulkSelectionCount}
              canMove={canBulkMove}
              canDelete={canBulkDelete}
              onMoveClick={bulkMove.open}
              onDeleteClick={bulkDelete.open}
            />
          ) : (
            <>
              {showOpenInNewTab && clickedItem && <OpenInNewTabMenuItem item={clickedItem} onClose={onClose} />}
              {hasCopyValue && columns && (
                <CopyMenuItem columns={columns} row={clickedItem} field={clickedField} onCopy={onClose} />
              )}
              {showNewItemContent && (
                <NewItemMenuContent
                  onClose={onClose}
                  onNewFolderClick={() => singleItemModals.createFolderModal.open(getTargetFolder())}
                  onFileUpload={handleFileUpload}
                  onFolderUpload={handleFolderUpload}
                  sx={{ padding: 0 }}
                />
              )}
              {clickedItem?.contentType === 'folder' && (
                <FolderActionsMenuContent
                  folder={clickedItem as FolderType}
                  onClose={onClose}
                  onRenameClick={singleItemModals.renameFolderModal.open}
                  onMoveClick={singleItemModals.moveFolderModal.open}
                  sx={{ padding: 0 }}
                />
              )}
              {clickedItem?.contentType === 'asset' && (
                <AssetActionsMenuContent
                  asset={clickedItem as Asset}
                  onClose={onClose}
                  onMoveClick={singleItemModals.moveAssetModal.open}
                  sx={{ padding: 0 }}
                />
              )}
              {showDeleteItemContent && (
                <DeleteItemMenuContent
                  clickedItem={clickedItem}
                  onClose={onClose}
                  onDeleteClick={singleItemModals.openDeleteModal}
                  sx={{ padding: 0 }}
                />
              )}
            </>
          )}
        </BaseContextMenu>
      )}
      <SingleItemModals<T> {...singleItemModals} />
      <BulkActionModals<T> bulkMove={bulkMove} bulkDelete={bulkDelete} />
    </>
  );
};
